import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter, firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { StaffSessionService } from '../services/staff-session.service';
import { StaffAdminVerifyService } from '../services/staff-admin-verify.service';
import { staffMenuBarFeature } from '../constant/staff-menu-bar.feature';
import { STAFF_DEFAULT_SCREEN } from '../constant/staff-default-screen.config';
import { HeaderService } from '../../header/header.service';
import { BrandService } from '../../brand/brand.service';
import { version as appVersion } from '../../../../package.json';

declare var customToastr: any;

@Component({
    selector: 'app-staff-layout',
    templateUrl: './staff-layout.component.html',
    styleUrls: ['../shared/staff-common.css', './staff-layout.component.css'],
    providers: [HeaderService]
})
export class StaffLayoutComponent implements OnInit, OnDestroy {
    staffName = '';
    staffRole = '';
    staffInitials = '';
    staffImage = '';
    schoolName = '';
    menu = staffMenuBarFeature;

    // "My Classes" should read as active for the whole class-workspace
    // section (class overview + every tool tile inside it: diary,
    // attendance, chat, activity, timetable, students...), not just the
    // literal /staff/classes list — routerLinkActive's own prefix matching
    // can't express that since /staff/class/:id doesn't share a URL prefix
    // with /staff/classes.
    isInsideClassSection = false;
    private routerSub: Subscription | null = null;
    brand = this.brandService.brand;
    appVersion = appVersion;

    constructor(
        private staffSession: StaffSessionService,
        private router: Router,
        private headerService: HeaderService,
        private translate: TranslateService,
        public adminVerify: StaffAdminVerifyService,
        private brandService: BrandService
    ) {}

    // Static menu config just decides whether this item is a candidate for
    // the nav at all — the school's own TIME_TABLE_MANAGEMENT feature flag
    // (same one gating the Class Overview timetable tile) still has to be
    // enabled for it to actually show, since "My Timetable" has no meaning
    // if timetables are disabled for the class/school entirely.
    get showMyTimetable(): boolean {
        return this.menu.myTimetable && this.staffSession.isFeatureEnabled('TIME_TABLE_MANAGEMENT');
    }

    // School-level extension services (set at login, refreshed on every
    // Dashboard load) — same source/serviceName keys the admin web app's
    // menu.component.ts gates its nav on via checkServiceStatus.
    // Hourhive/Staff Attendance is intentionally hidden from the staff nav
    // for now (per product request), so it has no enabled-check getter here.
    get doorwayServiceEnabled(): boolean {
        return this.staffSession.isExtensionServiceEnabled('DOORWAY');
    }

    get trackifyServiceEnabled(): boolean {
        return this.staffSession.isExtensionServiceEnabled('TRACKIFY');
    }

    get nexusServiceEnabled(): boolean {
        return this.staffSession.isExtensionServiceEnabled('LIBRARY_MANAGEMENT');
    }

    // ---------- Menu gating (per the mobile app's Drawer.js) ----------
    // Same pattern as showMyTimetable above: the static build-time flag in
    // staff-menu-bar.feature.ts decides whether an item is a candidate for
    // the nav at all, and the school's real backend feature/extension-
    // service flags (and, for fee items, the staff's own role) decide
    // whether it's actually shown.

    get showBroadcast(): boolean {
        return this.menu.broadcast && this.staffSession.isFeatureEnabled('BROADCAST_VIEW');
    }

    get hasUnreadBroadcast(): boolean {
        return this.staffSession.getAnyUnreadBroadcast();
    }

    get showProfile(): boolean {
        return this.menu.profile && this.staffSession.isFeatureEnabled('USER_PROFILE_VIEW');
    }

    get showSearch(): boolean {
        return this.menu.search && this.staffSession.isFeatureEnabled('SEARCH_PROFILE');
    }

    get showVehicleManagement(): boolean {
        return this.menu.transport && this.staffSession.isFeatureEnabled('VEHICLE_MANAGEMENT');
    }

    get showAcademicCalendar(): boolean {
        return this.menu.academicCalendar && this.staffSession.isExtensionServiceEnabled('ACADEMIC_CALENDAR');
    }

    get showStudentCheckIn(): boolean {
        return this.menu.checkInCheckOut && this.staffSession.isExtensionServiceEnabled('STUDENT_CHECK_IN_CHECK_OUT');
    }

    // Fee Collection / Overdue Report / Pending Report all require both the
    // school-level feature flag AND the staff member holding one of the two
    // fee-admin roles — permission and role are ANDed together, not either
    // alone.
    get showFeeCollection(): boolean {
        if (!this.menu.feesCollection || !this.staffSession.isFeatureEnabled('SCHOOL_ADMIN_FEE_COLLECTION_VIEW')) {
            return false;
        }
        const info = this.staffSession.getSchoolAndStaffInfo();
        const staffDetails = this.staffSession.getStaffDetails();
        const isFinanceAdmin = (info?.isFinanceAdmin ?? staffDetails?.isFinanceAdmin) === 1;
        const isFeeDefaulterAdmin = (info?.isFeeDefaulterAdmin ?? staffDetails?.isFeeDefaulterAdmin) === 1;
        return isFinanceAdmin || isFeeDefaulterAdmin;
    }

    get showFeeReports(): boolean {
        // Overdue Report and Pending Report share the same gate as Fee
        // Collection (per spec) — feesReports is its own build-time flag
        // (a separate menu item from feesCollection), but the school
        // feature/role check is identical.
        if (!this.menu.feesReports || !this.staffSession.isFeatureEnabled('SCHOOL_ADMIN_FEE_COLLECTION_VIEW')) {
            return false;
        }
        const info = this.staffSession.getSchoolAndStaffInfo();
        const staffDetails = this.staffSession.getStaffDetails();
        const isFinanceAdmin = (info?.isFinanceAdmin ?? staffDetails?.isFinanceAdmin) === 1;
        const isFeeDefaulterAdmin = (info?.isFeeDefaulterAdmin ?? staffDetails?.isFeeDefaulterAdmin) === 1;
        return isFinanceAdmin || isFeeDefaulterAdmin;
    }

    // "View Staff Attendance" — an in-app list of the whole school's staff
    // check-in/out log, sourced from Hourhive. Distinct from the HOURHIVE
    // *external service* shortcut below (which launches the Hourhive
    // product itself and is intentionally hidden from the nav) — this is a
    // normal in-app screen, gated on the same HOURHIVE extension-service
    // flag plus Super Admin role.
    get showViewStaffAttendance(): boolean {
        if (!this.menu.viewStaffAttendance || !this.staffSession.isExtensionServiceEnabled('HOURHIVE')) {
            return false;
        }
        const info = this.staffSession.getSchoolAndStaffInfo();
        const staffDetails = this.staffSession.getStaffDetails();
        return !!(info?.isSuperAdmin ?? staffDetails?.isSuperAdmin);
    }

    // Per spec (Drawer.js:110-114): shown only when the backend explicitly
    // sends ActiveStatus: "ENABLED" for this feature — no role check, no
    // fallback, defaults to hidden otherwise.
    get showLeaveManagement(): boolean {
        return this.menu.leaveManagement && this.staffSession.isFeatureExplicitlyEnabled('STUDENT_LEAVE_MANAGEMENT');
    }

    // Extension-service products (Hourhive, Doorway, Trackify, Nexus/Library
    // Management) are gated by generate/token/product, which lives on the
    // admin server and only accepts an admin-issued token — the staff
    // session token (from the separate staff server) 401s there. So this
    // re-verifies via StaffAdminVerifyService (mobile+password prompt,
    // reusing a cached admin token if one already exists) before proceeding.
    private readonly extensionServiceLabels: { [key: string]: string } = {
        HOURHIVE: 'Staff Attendance',
        DOORWAY: 'Visitor Management',
        TRACKIFY: 'Trackify',
        LIBRARY_MANAGEMENT: 'Library Management',
    };

    async openExternalService(serviceName: string) {
        const label = this.extensionServiceLabels[serviceName] || serviceName;
        const verified = await this.adminVerify.requestVerification(label);
        if (verified) {
            await this.launchExternalService(serviceName);
        }
    }

    // Opens an external extension-service product in a new tab, mirroring
    // the admin web app's menu.component.ts openExternalService/buildServiceURL.
    private async launchExternalService(serviceName: string) {
        try {
            const storageKey = serviceName.toLowerCase() + 'ServiceDetails';
            const response = await firstValueFrom(this.headerService.generateTokenForService(serviceName));
            const data = response as any;
            if (data?.status) {
                const { serviceURL, token } = data.body;
                const finalURL = this.buildServiceURL(serviceURL, token);
                localStorage.setItem(storageKey, JSON.stringify(data.body));
                window.open(finalURL, '_blank');
            } else {
                const message = await firstValueFrom(this.translate.get('failedToLoadServicePleaseTryAgain'));
                customToastr(message, 'error');
            }
        } catch {
            const message = await firstValueFrom(this.translate.get('oopsSomethingWentWrongPleaseTryAgain'));
            customToastr(message, 'error');
        }
    }

    private buildServiceURL(serviceURL: string, token: string): string {
        if (serviceURL.includes('#')) {
            const [baseURL, hash] = serviceURL.split('#');
            const [hashPath, hashQuery] = hash.split('?');
            const newHashQuery = new URLSearchParams(hashQuery || '');
            newHashQuery.set('token', token);
            return `${baseURL}#${hashPath}?${newHashQuery.toString()}`;
        } else {
            const url = new URL(serviceURL);
            url.searchParams.set('token', token);
            return url.toString();
        }
    }

    // The logo always needs somewhere valid to go — /staff/dashboard is only
    // valid when that menu item is actually enabled, so fall back to the
    // configured default screen otherwise (mirrors the login redirect logic).
    goToDefaultScreen() {
        this.router.navigate(['/staff', this.menu.dashboard ? 'dashboard' : STAFF_DEFAULT_SCREEN]);
    }

    ngOnInit(): void {
        // Deliberately no backfill of localStorage.usertoken from the
        // usertoken cookie here: that cookie holds the *staff-server*
        // session token (see establishStaffSession in
        // new-login-with-domain-verification.component.ts), not an
        // admin-issued token. HeaderService.generateTokenForService (used by
        // openExternalService) needs a real admin token in
        // localStorage.usertoken, which StaffAdminVerifyService is
        // responsible for obtaining (via requestVerification's modal) and
        // storing. Backfilling the staff token here used to make
        // requestVerification's "already verified" check pass immediately,
        // so the verify-mobile modal never appeared and the extension
        // service call 401'd against the admin server.

        // schoolAndStaffInfo (from the class-list API) is fresher and richer
        // than the OTP-verify response, so prefer it once it's available.
        const info = this.staffSession.getSchoolAndStaffInfo();
        const staffDetails = this.staffSession.getStaffDetails();
        const name = info?.staffName || staffDetails?.staffName || '';
        const role = info?.designation || staffDetails?.designation || '';
        if (name) {
            this.staffName = name;
            this.staffRole = role;
            this.staffInitials = this.staffSession.getInitials(name);
        }
        this.staffImage = info?.staffImage || '';
        this.schoolName = info?.schoolName || staffDetails?.schoolName || '';

        this.updateClassSectionActive(this.router.url);
        this.routerSub = this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe(event => this.updateClassSectionActive(event.urlAfterRedirects));
    }

    ngOnDestroy(): void {
        this.routerSub?.unsubscribe();
    }

    private updateClassSectionActive(url: string) {
        this.isInsideClassSection = /^\/staff\/(classes|class\/)/.test(url);
        this.mobileSidebarOpen = false;
    }

    // Small-screen sidebar drawer — same pattern as parent-login's
    // mobileSidebarOpen/toggleMobileSidebar/closeMobileSidebar, ported here
    // since the sidebar is otherwise just hidden below 900px with no way to
    // reach the full nav (only the bottom nav's subset of items).
    mobileSidebarOpen = false;

    toggleMobileSidebar(): void {
        this.mobileSidebarOpen = !this.mobileSidebarOpen;
    }

    closeMobileSidebar(): void {
        this.mobileSidebarOpen = false;
    }

    showLogoutConfirm = false;

    requestLogout() {
        this.showLogoutConfirm = true;
    }

    cancelLogout() {
        this.showLogoutConfirm = false;
    }

    confirmLogout() {
        this.showLogoutConfirm = false;
        this.staffSession.logout();
    }
}
